<script lang="ts">
	import type { Column, SortDirection } from '@isoftdata/svelte-table'
	import type { SvelteAsr, Mediator } from 'types/common'
	import type { i18n } from 'types/common'

	import { graphql, type MergeCustomerOptionsInput } from '$houdini'
	import { getContext } from 'svelte'
	import { PersistedState } from 'runed'
	import { hasPermission } from 'utility/permission'
	import { stringToBoolean } from '@isoftdata/utility-string'
	import { perPageCounts, type PerPageCount } from 'utility/search-fields'
	import { formatCustomerCurrency, type CustomerSearchResult, customerBalanceStatusMap } from 'utility/customer-helper'

	import Button from '@isoftdata/svelte-button'
	import Checkbox from '@isoftdata/svelte-checkbox'
	import Select from '@isoftdata/svelte-select'
	import Table, { Td } from '@isoftdata/svelte-table'
	import BooleanIndicator from 'components/BooleanIndicator.svelte'
	import MergeEntityModal, { type Entity } from 'components/MergeEntityModal.svelte'

	interface Props {
		asr: SvelteAsr
		customers: Array<CustomerSearchResult>
		showActiveCustomersOnly: boolean
		perPageCount: PerPageCount
		currentPageNumber?: number
		totalItemsCount?: number | undefined
		orderByColumn: string | undefined
		orderByDirection: SortDirection | undefined
	}

	let {
		asr,
		customers = $bindable(),
		showActiveCustomersOnly = true,
		perPageCount = $bindable(),
		currentPageNumber = 1,
		totalItemsCount = undefined,
		orderByColumn,
		orderByDirection,
	}: Props = $props()

	const { t: translate } = getContext<i18n>('i18next')

	const mediator = getContext<Mediator>('mediator')

	let mergeEntityModal: MergeEntityModal
	let selectedCustomers = new PersistedState<Array<Entity>>('selectedCustomers', [], {
		storage: 'session',
	})

	// TODO:
	// - add title on all the columns, could get it from the desktop

	const columns: Array<Column<CustomerSearchResult>> = [
		{
			name: '',
			property: 'isSelected',
			icon: 'check',
			align: 'center',
			sortType: false,
		},
		{
			name: translate('common:id', 'ID'),
			property: 'id',
			title: '',
			align: 'right',
			sortType: 'ALPHA_NUM',
		},
		{
			name: translate('common:store', 'Store'),
			property: 'store[code]',
			title: translate('customerSearch.theCustomersDefaultStore', "The customer's default store"),
			align: 'right',
			sortType: 'ALPHA_NUM',
		},
		{
			name: translate('common:companyName', 'Company Name'),
			property: 'companyName',
			title: '',
		},
		{
			name: translate('common:contactName', 'Contact Name'),
			property: 'contactName',
			title: '',
		},
		{
			name: translate('common:phoneNumber', 'Phone Number'),
			property: 'phoneNumber',
			title: '',
			sortType: false,
		},
		{
			name: translate('common:faxNumber', 'Fax Number'),
			property: 'faxNumber',
			title: '',
			sortType: false,
		},
		{
			name: translate('common:mobileNumber', 'Mobile Number'),
			property: 'mobilePhoneNumber',
			title: '',
			sortType: false,
		},
		{
			name: translate('common:balance', 'Balance'),
			property: 'balance',
			title: '',
			align: 'right',
			sortType: 'ALPHA_NUM',
		},
		{
			name: translate('common:type', 'Type'),
			property: 'type',
			title: '',
		},
		{
			name: translate('customer.dateEntered', 'Date Entered'),
			property: 'dateEntered',
			title: '',
			align: 'right',
		},
		{
			name: translate('common:streetAddress', 'Street Address'),
			property: 'billingAddress[address1]',
			title: '',
		},
		{
			name: translate('common:city', 'City'),
			property: 'billingAddress[city]',
			title: '',
		},
		{
			name: translate('common:state', 'State'),
			property: 'billingAddress[state]',
			title: '',
		},
		{
			name: translate('common:zip', 'Zip'),
			property: 'billingAddress[zip]',
			title: '',
			align: 'right',
		},
		{
			name: translate('common:country', 'Country'),
			property: 'billingAddress[country]',
			title: '',
		},
		{
			name: translate('common:emailAddress', 'Email Address'),
			property: 'email',
			title: '',
		},
		{
			name: translate('common:webAddress', 'Web Address'),
			property: 'webAddress',
			title: '',
		},
		{
			name: translate('common:defaultTerm', 'Default Term'),
			property: 'defaultTerm[name]',
			title: translate('customerSearch.theCustomersDefaultInvoiceTerms', "The customer's default invoice terms"),
		},
		{
			name: translate('common:salesperson', 'Salesperson'),
			property: 'salesPerson[name]',
			title: translate('customerSearch.thePrimarySalespersonOnTheAccount', 'The primary salesperson on the account'),
		},
		{
			name: translate('common:paymentMethod', 'Payment Method'),
			property: 'defaultPaymentMethod[name]',
			title: translate('customerSearch.theCustomersDefaultPaymentMethod', "The customer's default payment method"),
		},
		...getCustomerOptionColumns(customers[0]),
	]

	let sortColumn = $state(columns.find(column => column.property === orderByColumn))

	const mergeCustomerMutation = graphql(`
		mutation MergeCustomers($input: MergeCustomersInput!) {
			mergeCustomers(input: $input) {
				id
			}
		}
	`)

	const isCustomerOptions = (options: Record<string, unknown>): options is MergeCustomerOptionsInput =>
		'salesOrders' in options

	function getCustomerOptionColumns(customer: CustomerSearchResult) {
		if (!customer) {
			return []
		}
		const customerOptionColumns: Array<Column> = []
		customer.optionValues.forEach((optionValue, index) => {
			if (optionValue.option.showInCustomerList) {
				customerOptionColumns.push({
					property: `optionValues[${index}][value]`,
					name: optionValue.option.label || translate('customerSearch.customField', 'Custom Field'),
					align:
						(optionValue.option.dataType === 'CURRENCY' && 'right') ||
						(optionValue.option.dataType === 'BOOLEAN' && 'center') ||
						undefined,
					sortType: false,
				})
			}
		})
		return customerOptionColumns
	}

	function updateSelectedCustomerIds(customer: CustomerSearchResult, isSelected: boolean) {
		if (isSelected) {
			selectedCustomers.current.push({
				id: customer.id,
				active: customer.active,
				storeCode: customer.store?.code || null,
				companyName: customer.companyName,
				contactName: customer.contactName,
				email: customer.email,
				phoneNumber: customer.phoneNumber,
				streetAddress: customer.billingAddress.address1,
			})
		} else {
			selectedCustomers.current = selectedCustomers.current.filter(
				selectedCustomer => selectedCustomer.id !== customer.id,
			)
		}
	}

	async function mergeCustomer(
		masterCustomerId: number,
		sourceCustomerIds: Array<number>,
		mergeOptions: MergeCustomerOptionsInput,
	) {
		const { data } = await mergeCustomerMutation.mutate({
			input: {
				targetCustomerId: masterCustomerId.toString(),
				sourceCustomerIds: sourceCustomerIds.map(id => id.toString()),
				mergeOptions,
			},
		})

		if (!data) {
			throw new Error('Failed to merge customers')
		}

		selectedCustomers.current = []
		mediator.publish('showMessage', {
			type: 'success',
			heading: translate('vendorSearch.mergedSuccessful!', 'Merged Successful!'),
			message: translate(
				'customerSearch.customersHaveBeenMerged',
				'{{count}} customers have been merged into customer ID-{{masterCustomerId}}',
				{ count: sourceCustomerIds.length, masterCustomerId },
			),
			time: 10,
		})
		asr.go(
			null,
			{ pageNumber: currentPageNumber, perPageCount, orderByColumn, orderByDirection, lastSavedTime: new Date() },
			{ inherit: true },
		)
	}
</script>

<div class="card-body">
	<div class="row justify-content-between align-items-end mt-3 mx-1">
		<div class="col-auto">
			<Checkbox
				inline
				label={translate('customerSearch.showActiveCustomersOnly', 'Show Active Customers Only')}
				checked={showActiveCustomersOnly}
				onchange={() => {
					asr.go(null, { showActiveCustomersOnly: !showActiveCustomersOnly }, { inherit: true })
				}}
			/>
		</div>
		<div class="col-auto">
			<Select
				label={translate('common:resultsPerPage', 'Results Per Page')}
				labelClass="me-2 text-nowrap"
				labelParentClass="d-flex align-items-center mb-0"
				showEmptyOption={false}
				bind:value={perPageCount}
				onchange={() => {
					asr.go(null, { perPageCount, pageNumber: 1 }, { inherit: true })
				}}
			>
				{#each perPageCounts as perPageCount}
					<option value={perPageCount}>{perPageCount}</option>
				{/each}
			</Select>
		</div>
	</div>
	<Table
		responsive
		stickyHeader
		columnHidingEnabled
		columnResizingEnabled
		{columns}
		{currentPageNumber}
		{totalItemsCount}
		{sortColumn}
		parentStyle="max-height: 60vh"
		localStorageKey="customer-search-results"
		sortDirection={orderByDirection}
		rows={customers}
		perPageCount={parseInt(perPageCount, 10)}
		columnClickedMethod={(column, direction) => {
			asr.go(null, { orderByColumn: column.property, orderByDirection: direction }, { inherit: true })
			return Promise.resolve()
		}}
		pageChange={({ pageNumber }) => asr.go(null, { pageNumber: pageNumber }, { inherit: true })}
	>
		{#snippet noRows({ visibleColumnsCount })}
			<tr>
				<td
					class="text-center"
					colspan={visibleColumnsCount}>{translate('customerSearch.noCustomersFound', 'No customers found')}</td
				>
			</tr>
		{/snippet}
		{#snippet children({ row: customer })}
			{@const isSelected = selectedCustomers.current.find(selectedCustomer => selectedCustomer.id === customer.id)}
			<tr
				class="cursor-pointer"
				class:text-body-secondary={!customer.active}
				onclick={() => asr.go('app.customer', { customerId: customer.id })}
			>
				<Td
					property="isSelected"
					onclick={event => {
						event.stopPropagation()
						if (event.target && (event.target as HTMLElement).id !== `customer-${customer.id}`) {
							updateSelectedCustomerIds(customer, !isSelected)
						}
					}}
				>
					<Checkbox
						showLabel={false}
						id={`customer-${customer.id}`}
						checked={!!isSelected}
						onchange={() => {
							updateSelectedCustomerIds(customer, !isSelected)
						}}
					/>
				</Td>
				<Td property="id">{customer.id}</Td>
				<Td property="store[code]">{customer.store?.code ?? ''}</Td>
				<Td property="companyName">{customer.companyName}</Td>
				<Td property="contactName">{customer.contactName}</Td>
				<Td
					property="phoneNumber"
					class="text-nowrap"
				>
					{customer.phoneNumber}
				</Td>
				<Td
					property="faxNumber"
					class="text-nowrap"
				>
					{customer.faxNumber}
				</Td>
				<Td property="mobilePhoneNumber">{customer.mobilePhoneNumber}</Td>
				<Td property="balance">
					<span class={customer.active ? customerBalanceStatusMap[customer.balanceStatus].textColor : ''}
						>{formatCustomerCurrency(customer.balance)}</span
					>
				</Td>
				<Td property="type">{customer.type}</Td>
				<Td property="dateEntered"
					>{customer.dateEntered
						? customer.dateEntered.toLocaleDateString()
						: translate('common:notSlashAcceptable', 'N/A')}</Td
				>
				<Td property="billingAddress[address1]">{customer.billingAddress.address1}</Td>
				<Td property="billingAddress[city]">{customer.billingAddress.city}</Td>
				<Td property="billingAddress[state]">{customer.billingAddress.state}</Td>
				<Td property="billingAddress[zip]">{customer.billingAddress.zip}</Td>
				<Td property="billingAddress[country]">{customer.billingAddress.country}</Td>
				<Td property="email">{customer.email}</Td>
				<Td property="webAddress">{customer.webAddress}</Td>
				<Td property="defaultTerm[name]">{customer.defaultTerm?.name ?? ''}</Td>
				<Td property="salesPerson[name]">{customer.salesPerson?.name ?? ''}</Td>
				<Td property="defaultPaymentMethod[name]">{customer.defaultPaymentMethod?.name ?? ''}</Td>
				{#each customer.optionValues as optionValue, index}
					{#if optionValue.option.showInCustomerList}
						<Td
							property={`optionValues[${index}][value]`}
							class={optionValue.option.dataType === 'CURRENCY' ? 'text-end' : ''}
						>
							{#if optionValue.option.dataType === 'CURRENCY'}
								{optionValue.value ? formatCustomerCurrency(optionValue.value) : ''}
							{:else if optionValue.option.dataType === 'DATE'}
								{optionValue.value ? new Date(optionValue.value).toLocaleDateString() : ''}
							{:else if optionValue.option.dataType === 'BOOLEAN'}
								<BooleanIndicator value={stringToBoolean(optionValue.value)} />
							{:else}
								{optionValue.value}
							{/if}
						</Td>
					{/if}
				{/each}
			</tr>
		{/snippet}
	</Table>
</div>
<div class="card-footer d-flex justify-content-between align-items-center">
	<div>
		<Button
			outline
			size="sm"
			iconClass="merge"
			disabled={!hasPermission('CUSTOMERS_CAN_MERGE') || selectedCustomers.current.length < 2}
			onclick={() => mergeEntityModal.open(selectedCustomers.current)}
		>
			{translate('customerSearch.mergeCustomers', 'Merge Customers')}
		</Button>
		{#if selectedCustomers.current.length > 0}
			<Button
				outline
				size="sm"
				color="warning"
				iconClass="broom"
				onclick={() => {
					selectedCustomers.current = []
				}}
			>
				Clear Selection
			</Button>
		{/if}
	</div>
	<span
		>{totalItemsCount
			? `${totalItemsCount} ${translate('customerSearch.customersFound', 'Customers Found')}`
			: translate('customerSearch.noCustomersFound', 'No customers found')}</span
	>
</div>

<MergeEntityModal
	entityType="customer"
	title={translate('customerSearch.mergeCustomers', 'Merge Customers')}
	bind:this={mergeEntityModal}
	mergeFn={async (masterCustomerId, sourceCustomerIds, mergeOptions) => {
		if (isCustomerOptions(mergeOptions)) {
			await mergeCustomer(masterCustomerId, sourceCustomerIds, mergeOptions)
		}
	}}
/>

<style>
	* :global(.text-dark-goldenrod) {
		color: var(--bs-warning-text-emphasis);
	}
</style>
